import { useMemo, useState, useCallback, useEffect } from "react";
import { format } from "date-fns";
import { useNotificationContext } from "cerulean";
import { modules, entities, parseUrlLabel } from "byzantine";
import Filters from "byzantine/src/filters";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";

import MerchantIcon from "../MerchantIcon";
import { useSubscriptionCards } from "../../contexts/SubscriptionCardsContext";
import Section from "./Section";
import Detail from "./Detail";
import CancelSubscriptionCard from "./CancelSubscriptionCard";
import SubscriptionCancelInfoDialog from "./SubscriptionCancelInfoDialog";

import styles from "./MerchantDetails.module.scss";

export interface MerchantDetailsProps {
  cardId: API.CardId;
  transactionUuid: string;
}

const MerchantDetails = ({ cardId, transactionUuid }: MerchantDetailsProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();
  const transaction = entities.subscriptions.useTransactionByUuid(
    cardId,
    transactionUuid
  );

  const { cards } = useSubscriptionCards();
  const card = useMemo(
    () => cards.find((c) => c.id === cardId),
    [cards, cardId]
  );

  useEffect(() => {
    if (!transaction) {
      sendNotification({
        type: "negative",
        text: "Unable to find that subscription.",
      });
      navigate(`/cards/${cardId}/subscriptions`, { replace: true });
    }
  }, [navigate, transaction, cardId, sendNotification]);

  const merchantName = transaction?.merchant_name ?? "";
  const merchant = entities.merchants.useMerchant(merchantName);
  const subscription = entities.subscriptionMetadata.useSubscriptionById(
    transaction?.subscription_id ?? ""
  );

  const { paymentHistory } =
    modules.subscriptions.useSubscriptionPaymentHistory(
      cardId,
      subscription?.id ?? ""
    );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = useCallback(() => setIsDialogOpen(true), []);
  const closeDialog = useCallback(() => setIsDialogOpen(false), []);

  const sectionMerchantTitle = l10n.getString(
    "merchant-details-section-merchant-details",
    null,
    "MERCHANT DETAILS"
  );
  const sectionHistoryTitle = l10n.getString(
    "merchant-details-section-history",
    null,
    "HISTORY"
  );
  const labelWebsite = l10n.getString(
    "merchant-details-label-website",
    null,
    "Website"
  );
  const prettyWebsiteUrl = merchant?.website_url
    ? parseUrlLabel(merchant.website_url)
    : merchant?.website_url;
  const labelCard = l10n.getString("merchant-details-label-card", null, "Card");
  const labelNext = l10n.getString(
    "merchant-details-label-estimated-next-payment",
    null,
    "Estimated next payment"
  );
  const nextPaymentDate = subscription?.next_payment_date?.trim();
  const isCancelled = subscription?.state === "cancelled";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.logoWrapper}>
          <MerchantIcon name={merchantName} />
        </div>
        <h2>{merchantName}</h2>
      </div>
      <Section title={sectionMerchantTitle}>
        {merchant?.website_url && (
          <Detail
            label={labelWebsite}
            value={
              <a
                href={merchant.website_url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {prettyWebsiteUrl}
              </a>
            }
          />
        )}
        <Detail label={labelCard} value={card?.name ?? ""} />
        {nextPaymentDate && !isCancelled && (
          <Detail
            label={labelNext}
            value={format(new Date(nextPaymentDate), "MMMM d, yyyy")}
          />
        )}
      </Section>
      <Section title={sectionHistoryTitle}>
        <div className={styles.historyList}>
          {paymentHistory.map((tx) => (
            <Detail
              key={tx.id}
              label={format(tx.transaction_date, "MMMM d, yyyy")}
              value={Filters.currency(Filters.dollarsToPennies(tx.amount))}
            />
          ))}
        </div>
      </Section>
      {!isCancelled && (
        <>
          <CancelSubscriptionCard
            subscriptionId={transaction?.subscription_id}
            openCancelSubscriptionDialog={openDialog}
          />
          <SubscriptionCancelInfoDialog
            merchantName={merchantName}
            isOpen={isDialogOpen}
            onUserDismiss={closeDialog}
          />
        </>
      )}
    </div>
  );
};

export default MerchantDetails;
